import React, { useEffect, useRef, useState, CSSProperties } from 'react';

/**
 * 轻量级 Scroll-Reveal 组件
 * 使用 IntersectionObserver 在元素进入视口时触发 CSS 动画
 * 灵感来自 Linear / Vercel 的淡入效果
 */

interface RevealProps {
  children: React.ReactNode;
  /** 动画延迟 ms */
  delay?: number;
  /** 入场方向 */
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
  /** 入场距离 px */
  distance?: number;
  /** 动画时长 ms */
  duration?: number;
  /** 是否只触发一次 */
  once?: boolean;
  /** 额外 className */
  className?: string;
  /** 额外 style */
  style?: CSSProperties;
  /** threshold 0~1 */
  threshold?: number;
}

const Reveal: React.FC<RevealProps> = ({
  children,
  delay = 0,
  direction = 'up',
  distance = 24,
  duration = 600,
  once = true,
  className = '',
  style = {},
  threshold = 0.1,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // 尊重用户的 prefers-reduced-motion
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) observer.unobserve(el);
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold, rootMargin: '0px 0px -40px 0px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [once, threshold]);

  const translateMap = {
    up: `translateY(${distance}px)`,
    down: `translateY(-${distance}px)`,
    left: `translateX(${distance}px)`,
    right: `translateX(-${distance}px)`,
    none: 'none',
  };

  const mergedStyle: CSSProperties = {
    ...style,
    opacity: visible ? 1 : 0,
    transform: visible ? 'translate3d(0,0,0)' : translateMap[direction],
    transition: `opacity ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
    willChange: visible ? 'auto' : 'opacity, transform',
  };

  return (
    <div ref={ref} className={className} style={mergedStyle}>
      {children}
    </div>
  );
};

/**
 * StaggerChildren — 为直接子元素添加递增延迟的 Reveal 父容器
 */
interface StaggerProps {
  children: React.ReactNode;
  /** 每个子元素之间的延迟 ms */
  stagger?: number;
  /** 基础延迟 */
  baseDelay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
  distance?: number;
  duration?: number;
  className?: string;
  style?: CSSProperties;
}

const StaggerReveal: React.FC<StaggerProps> = ({
  children,
  stagger = 60,
  baseDelay = 0,
  direction = 'up',
  distance = 20,
  duration = 500,
  className = '',
  style = {},
}) => {
  const items = React.Children.toArray(children);

  return (
    <div className={className} style={style}>
      {items.map((child, i) => (
        <Reveal
          key={i}
          delay={baseDelay + i * stagger}
          direction={direction}
          distance={distance}
          duration={duration}
        >
          {child}
        </Reveal>
      ))}
    </div>
  );
};

export { Reveal, StaggerReveal };
export default Reveal;
